<template>
  <div ref="reference" class="relative w-full">
    <!-- Selected state: a filled field mirroring the Input xl look. -->
    <div
      v-if="selection"
      class="relative w-full rounded-[6px] border border-gray-200 bg-white px-[16px] pt-[21px] pb-[7px]"
      :class="disabled && 'pointer-events-none opacity-50'"
    >
      <span
        class="pointer-events-none absolute left-[16px] top-1 origin-[0] scale-75 text-[14px] font-medium leading-[18px] text-gray-500/70"
      >
        {{ label }}
      </span>
      <div class="flex items-center gap-[8px] pr-[24px]">
        <Avatar
          v-if="selectedAvatar"
          :src="selectedAvatar"
          :size="18"
          class="shrink-0"
        />
        <OrganizationIcon
          v-else
          class="size-[15px] shrink-0 text-gray-800"
          aria-hidden="true"
        />
        <span
          class="min-w-0 flex-1 truncate text-[14px] leading-[18px] text-gray-800"
        >
          {{ selectedName }}
        </span>
      </div>
      <button
        type="button"
        aria-label="Clear organization"
        class="absolute right-[16px] top-1/2 flex -translate-y-1/2 text-gray-500 transition-colors hover:text-gray-800"
        @click="handleClear"
      >
        <CloseIcon class="size-[16px]" aria-hidden="true" />
      </button>
    </div>

    <!-- Search state -->
    <template v-else>
      <Input
        v-model="text"
        size="xl"
        :label="label"
        :disabled="disabled"
        autocomplete="off"
        @focus="handleFocus"
      />

      <Teleport to="body">
        <Transition v-bind="transition">
          <div
            v-if="showResults"
            ref="floating"
            class="z-[999]"
            :style="floatingStyles"
          >
            <Menu class="max-h-[260px] overflow-y-auto" @scroll="handleScroll">
              <Menu.Section v-if="results.length"> Suggested </Menu.Section>

              <template v-if="loading && !results.length">
                <div
                  v-for="n in 3"
                  :key="n"
                  class="flex items-center gap-[10px] px-[10px] py-[4px]"
                >
                  <Skeleton class="size-7 shrink-0 rounded-full" />
                  <div class="flex flex-1 flex-col gap-[4px]">
                    <Skeleton class="h-[12px] w-1/2 rounded-[4px]" />
                    <Skeleton class="h-[10px] w-2/3 rounded-[4px]" />
                  </div>
                </div>
              </template>

              <Menu.Item
                v-for="result in results"
                :key="result.org.id"
                size="large"
                :avatar="result.org.avatarUrl || undefined"
                :icon="ProfileIcon"
                @click="handleSelect(result.org)"
              >
                {{ result.org.name }}
                <template v-if="result.description" #description>
                  {{ result.description }}
                </template>
              </Menu.Item>

              <Menu.Item
                v-if="text.trim() && !loading"
                size="large"
                :icon="OrganizationIcon"
                @click="handleUseTyped"
              >
                Create "{{ text }}"
              </Menu.Item>
            </Menu>
          </div>
        </Transition>
      </Teleport>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import { Avatar, Input, Menu, Skeleton } from '@dacr/library'
import { CloseIcon, OrganizationIcon, ProfileIcon } from '@dacr/icons'

import {
  EntityType,
  useSearchEntitiesLazyQuery,
  type Entity,
  type Organization,
} from '@/shared/graphql/legacy/generated'
import { useFloatingMenu } from '@/shared/composables/useFloatingMenu'

type Selection = { kind: 'entity'; org: Entity } | { kind: 'new'; name: string }

const props = defineProps<{
  modelValue?: Entity
  label: string
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', payload?: Entity): void
  (e: 'update:query', payload: string): void
}>()

const query = ref('')
const variables = ref({
  query,
  filterBy: EntityType.Organization,
})

const { result, loading, load, fetchMore } =
  useSearchEntitiesLazyQuery(variables)

const text = ref('')
const selection = ref<Selection | undefined>(
  props.modelValue ? { kind: 'entity', org: props.modelValue } : undefined
)
const showResults = ref(false)

const { reference, floating, floatingStyles, transition } =
  useFloatingMenu(showResults)

const selectedName = computed(() => {
  if (!selection.value) return ''
  return selection.value.kind === 'entity'
    ? selection.value.org.name
    : selection.value.name
})

const selectedAvatar = computed(() =>
  selection.value?.kind === 'entity' ? selection.value.org.avatarUrl : undefined
)

const results = computed(() =>
  (result.value?.searchEntities.entities ?? []).slice(0, 4).map((entity) => {
    const org = entity as Entity
    const owner = (org as Organization).owner?.name

    return {
      org,
      description: owner ? `Managed by ${owner}` : '',
    }
  })
)

const search = (value: string) => {
  query.value = value
  showResults.value = !!value.trim()

  if (value.trim()) load()
}

const handleFocus = () => {
  if (text.value.trim()) showResults.value = true
}

const handleSelect = (org: Entity) => {
  selection.value = { kind: 'entity', org }
  emit('update:modelValue', org)
  emit('update:query', '')
  showResults.value = false
}

const handleUseTyped = () => {
  const name = text.value.trim()
  selection.value = { kind: 'new', name }
  emit('update:modelValue', undefined)
  emit('update:query', name)
  showResults.value = false
}

const handleClear = () => {
  selection.value = undefined
  text.value = ''
  query.value = ''
  emit('update:modelValue', undefined)
  emit('update:query', '')
  showResults.value = false
  nextTick(() => reference.value?.querySelector('input')?.focus())
}

const fetchNextPage = () => {
  if (!result.value?.searchEntities.next || loading.value) return

  fetchMore({
    variables: {
      next: result.value.searchEntities.next,
    },
    updateQuery: (previousData, { fetchMoreResult: newData }) => {
      if (!newData) return previousData

      return {
        __typename: 'Query',
        searchEntities: {
          ...previousData.searchEntities,
          entities: [
            ...previousData.searchEntities.entities,
            ...newData.searchEntities.entities,
          ],
          next: newData.searchEntities.next,
        },
      }
    },
  })
}

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const scrollPos = element.clientHeight + element.scrollTop

  if (scrollPos / element.scrollHeight >= 0.5) {
    fetchNextPage()
  }
}

watch(text, search)
</script>
